import { Img } from '@filejet/sdk/react';
import { Box, SxProps, Theme } from '@mui/material';
import React from 'react';
import { getInitials } from '../../helpers';
import { Avatar } from '../userInterface';
import { CircularAvatar } from './circularAvatar';

export interface UserAvatarProps {
  /**
   * Full name of the user (used to derive initials)
   */
  readonly name: string;
  /**
   * Avatar size (maps to CircularAvatar size values)
   */
  readonly size?: 'xxxs' | 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl';
  /**
   * Custom styles (overrides default gradient)
   */
  readonly sx?: SxProps<Theme>;

  /**
   * Optional avatar image.
   * If provided, it renders the image using FileJet `<Img />`.
   * If `null` or undefined, the component displays user initials
   * on a gradient background instead.
   *
   * Example:
   * ```ts
   * avatar={{ id: 'filejet-id', hash: 'thumbhash' }}
   * ```
   */
  readonly avatar?: Avatar | null;
}

export function UserAvatar({ name, size = 's', avatar, sx }: UserAvatarProps) {
  const gradientBackground =
    'linear-gradient(225.4deg, #FFCBA0 0%, #DD5789 48.12%, #9B5BE6 93.59%)';
  if (avatar != null) {
    return (
      <Box
        sx={{
          img: {
            borderRadius: theme => theme.radius.full,
            borderColor: theme => theme.brandPalette.background.primary,
            borderWidth: '1px'
          }
        }}
      >
        <Img
          src={avatar.id}
          fit="cover"
          thumbhash={avatar.hash}
          alt="avatar"
          width={32}
          height={32}
        />
      </Box>
    );
  }

  return (
    <CircularAvatar
      size={size}
      sx={{
        background: gradientBackground,
        '& .MuiTypography-root': {
          color: '#fff',
          fontWeight: 400,
          fontSize: theme => theme.typography.labelSmall.fontSize
        },
        ...sx
      }}
    >
      {getInitials(name)}
    </CircularAvatar>
  );
}
